import AddHomeOutlinedIcon from "@mui/icons-material/AddHomeOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import { alpha, Box, Dialog, DialogContent, Fade, Typography, useTheme } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { ImobiliarioEdificacao } from "../../../interfaces";
import { DashboardContext } from "../../../providers/Dashboard";
import { getVinculosImobiliarioEdificacaoPorLote } from "../../../services/api";
import { vincularEdificacaoRioBrancoAsync } from "../../../services/data";
import { getAll } from "../../../services/db";
import {
  AcaoVinculoEdificacaoRioBranco,
  getSubunidadesAtivas,
  getVinculosAtivosDoImobiliario,
  imobiliarioTemPrincipalAtiva,
} from "../../../utils/vincularEdificacaoRioBranco";
import {
  IconComplementarSubunidade,
  OpcaoVinculoUi,
  vinculoRioBrancoDialogPaperProps,
  VinculoRioBrancoDialogActions,
  VinculoRioBrancoDialogChips,
  VinculoRioBrancoDialogHeader,
  VinculoRioBrancoDialogLoading,
  VinculoRioBrancoOpcoesList,
} from "../VinculoRioBrancoDialogUi";

interface Props {
  municipio: string;
  openDialog: boolean;
  handleCloseDialog: () => void;
  onVinculoConcluido: (resultado: {
    id_imobiliario: string;
    id_imobiliario_edificacao: string;
    id_edificacao: string;
    novoImobiliario?: boolean;
  }) => void;
}

type DialogMode = "principal" | "opcoes";

export function DialogVincularEdificacaoRioBranco({
  municipio,
  openDialog,
  handleCloseDialog,
  onVinculoConcluido,
}: Props) {
  const theme = useTheme();
  const { metadata } = useContext(DashboardContext);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [mode, setMode] = useState<DialogMode>("principal");
  const [vinculos, setVinculos] = useState<ImobiliarioEdificacao[]>([]);
  const [acao, setAcao] = useState<AcaoVinculoEdificacaoRioBranco>("subunidade_imovel");
  const [subunidadeAlvoId, setSubunidadeAlvoId] = useState<string>("");

  const idImobiliario = metadata.id_imobiliario;
  const idEdificacao = metadata.id_edificacao;
  const idLote = metadata.id_lote;

  const subunidades = useMemo(() => {
    if (!idImobiliario) return [];
    return getSubunidadesAtivas(vinculos, idImobiliario);
  }, [vinculos, idImobiliario]);

  const opcoes = useMemo<OpcaoVinculoUi[]>(() => {
    const lista: OpcaoVinculoUi[] = [
      {
        value: "subunidade_imovel",
        titulo: "Subunidade",
        descricao: "Vincula como subunidade da edificação principal do imobiliário.",
        icon: <ApartmentOutlinedIcon sx={{ fontSize: 20 }} />,
        accent: theme.palette.info.main,
      },
      {
        value: "complementar_principal",
        titulo: "Complementar da principal",
        descricao: "Vincula como complementar da edificação principal.",
        icon: <LinkOutlinedIcon sx={{ fontSize: 20 }} />,
        accent: theme.palette.warning.main,
      },
    ];

    if (subunidades.length > 0) {
      lista.push({
        value: "complementar_subunidade",
        titulo: "Complementar da subunidade",
        descricao: "Vincula como complementar de uma subunidade existente.",
        icon: <IconComplementarSubunidade />,
        accent: theme.palette.warning.main,
      });
    }

    lista.push({
      value: "nova_unidade",
      titulo: "Nova unidade",
      descricao: "Cria um novo imobiliário e define esta edificação como principal.",
      icon: <AddHomeOutlinedIcon sx={{ fontSize: 20 }} />,
      accent: theme.palette.success.main,
    });

    return lista;
  }, [subunidades.length, theme.palette]);

  useEffect(() => {
    if (!openDialog || municipio !== "riobranco" || !idImobiliario || !idEdificacao || !idLote) return;

    const carregarContexto = async () => {
      setLoading(true);
      try {
        const vinculosApi = await getVinculosImobiliarioEdificacaoPorLote(Number(idLote));
        const vinculosLocais = (getAll("imobiliario_edificacao") || []) as ImobiliarioEdificacao[];
        const mapa = new Map<string, ImobiliarioEdificacao>();

        [...vinculosLocais, ...vinculosApi].forEach((vinculo) => {
          if (vinculo?.id != null) {
            mapa.set(String(vinculo.id), vinculo as ImobiliarioEdificacao);
          }
        });

        const merged = Array.from(mapa.values());
        setVinculos(merged);

        const vinculosImobiliario = getVinculosAtivosDoImobiliario(merged, idImobiliario);
        const temPrincipal = imobiliarioTemPrincipalAtiva(merged, idImobiliario);

        if (vinculosImobiliario.length === 0 || !temPrincipal) {
          setMode("principal");
        } else {
          setMode("opcoes");
          setAcao("subunidade_imovel");
          const subs = getSubunidadesAtivas(merged, idImobiliario);
          setSubunidadeAlvoId(subs[0] ? String(subs[0].id) : "");
        }
      } catch (error) {
        console.error(error);
        toast.error("Não foi possível carregar os vínculos do imobiliário.");
        handleCloseDialog();
      } finally {
        setLoading(false);
      }
    };

    carregarContexto();
  }, [openDialog, municipio, idImobiliario, idEdificacao, idLote, handleCloseDialog]);

  async function confirmarVinculo(acaoSelecionada: AcaoVinculoEdificacaoRioBranco) {
    if (!idImobiliario || !idEdificacao || !idLote) return;

    if (acaoSelecionada === "complementar_subunidade" && !subunidadeAlvoId) {
      toast.error("Selecione a subunidade de referência.");
      return;
    }

    setProcessing(true);
    try {
      const resultado = await vincularEdificacaoRioBrancoAsync({
        id_edificacao: idEdificacao,
        id_imobiliario: idImobiliario,
        id_lote: idLote,
        acao: acaoSelecionada,
        id_imobiliario_edificacao_alvo:
          acaoSelecionada === "complementar_subunidade" ? subunidadeAlvoId : undefined,
      });

      onVinculoConcluido({
        id_imobiliario: resultado.id_imobiliario,
        id_imobiliario_edificacao: String(resultado.vinculo.id),
        id_edificacao: idEdificacao,
        novoImobiliario: acaoSelecionada === "nova_unidade",
      });
      handleCloseDialog();
      toast.success("Edificação vinculada com sucesso.");
    } catch (error) {
      console.error(error);
      toast.error("Não foi possível vincular a edificação.");
    } finally {
      setProcessing(false);
    }
  }

  const accentPrincipal = theme.palette.primary.main;

  return (
    <Dialog
      open={openDialog}
      onClose={processing ? undefined : handleCloseDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={vinculoRioBrancoDialogPaperProps}
    >
      <VinculoRioBrancoDialogHeader
        icon={
          mode === "principal" ? (
            <HomeOutlinedIcon sx={{ fontSize: 22 }} />
          ) : (
            <LinkOutlinedIcon sx={{ fontSize: 22 }} />
          )
        }
        title={mode === "principal" ? "Vincular como principal" : "Tipo de vínculo"}
        subtitle={
          mode === "principal"
            ? "Esta edificação será a principal do imobiliário."
            : "Escolha como a edificação se relaciona com o imobiliário."
        }
        onClose={handleCloseDialog}
        disabled={processing}
      />

      <VinculoRioBrancoDialogChips
        labels={[`Edificação #${idEdificacao}`, `Imobiliário #${idImobiliario}`]}
      />

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {loading ? (
          <VinculoRioBrancoDialogLoading message="Verificando vínculos..." />
        ) : (
          <Fade in={!loading}>
            <Box>
              {mode === "principal" ? (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: alpha(accentPrincipal, 0.2),
                    bgcolor: alpha(accentPrincipal, 0.04),
                  }}
                >
                  <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                    O imobiliário selecionado não possui nenhuma edificação principal ativa. A edificação{" "}
                    <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
                      #{idEdificacao}
                    </Typography>{" "}
                    deve ser vinculada como principal deste imobiliário.
                  </Typography>
                </Box>
              ) : (
                <VinculoRioBrancoOpcoesList
                  opcoes={opcoes}
                  acaoSelecionada={acao}
                  onSelectAcao={(value) => setAcao(value as AcaoVinculoEdificacaoRioBranco)}
                  subunidades={subunidades}
                  subunidadeAlvoId={subunidadeAlvoId}
                  onSubunidadeAlvoChange={setSubunidadeAlvoId}
                />
              )}
            </Box>
          </Fade>
        )}
      </DialogContent>

      <VinculoRioBrancoDialogActions
        onCancel={handleCloseDialog}
        onConfirm={() => confirmarVinculo(mode === "principal" ? "principal" : acao)}
        confirmLabel={mode === "principal" ? "Vincular como principal" : "Confirmar vínculo"}
        processingLabel="Vinculando..."
        loading={loading}
        processing={processing}
      />
    </Dialog>
  );
}
